import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { LifeWheel } from './CreateLifeWheel';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';

function ViewLifeWheel() {
  const { wheelId: urlWheelId } = useParams();
  const navigate = useNavigate();
  const [wheelId, setWheelId] = useState(urlWheelId || '');
  const [loading, setLoading] = useState(false);
  const [wheelData, setWheelData] = useState(null);
  const [error, setError] = useState('');

  const handleIdChange = (e) => {
    let value = e.target.value.toUpperCase();
    if (!value.startsWith('LW-') && value.length > 0) {
      value = 'LW-' + value.replace(/[^A-Z0-9]/g, '');
    } else {
      value = value.replace(/[^A-Z0-9-]/g, '');
    }
    if (value.length > 12) {
      value = value.slice(0, 12);
    }
    setWheelId(value);
    setError('');
  };

  const fetchWheelData = async (id) => {
    if (!id) {
      setError('Please enter a Life Wheel ID');
      return;
    }

    setLoading(true);
    setError('');
    setWheelData(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/wheels/${id}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch Life Wheel data');
      }
      
      const data = await response.json();
      console.log('Received wheel data:', data);
      
      if (!data.areas || !data.scores || data.areas.length !== data.scores.length) {
        throw new Error('Invalid Life Wheel data received');
      }
      
      setWheelData(data);

      // Update URL if not already there
      if (!urlWheelId) {
        navigate(`/view/${id}`, { replace: true });
      }
    } catch (err) {
      console.error('Error fetching wheel:', err);
      setError(err.message || 'Unable to find Life Wheel. Please check the ID and try again.');
      setWheelData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const id = wheelId.trim();
    await fetchWheelData(id);
  };

  // Auto-fetch when wheelId is provided in URL
  useEffect(() => {
    if (urlWheelId) {
      const id = urlWheelId.startsWith('LW-') ? urlWheelId : `LW-${urlWheelId}`;
      setWheelId(id);
      fetchWheelData(id);
    }
  }, [urlWheelId]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 2,
          minHeight: '60vh',
          justifyContent: 'center'
        }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="textSecondary">
            Loading Life Wheel...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        View Life Wheel
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!urlWheelId && (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Box sx={{ maxWidth: 400, mx: 'auto' }}>
            <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
              Enter your Life Wheel ID to view your assessment. You should have received this ID in your email after creating your Life Wheel.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              The ID should be in the format LW-XXXXXXXXX (e.g., LW-ABC123XYZ)
            </Typography>
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                placeholder="LW-ABC123XYZ"
                value={wheelId}
                onChange={handleIdChange}
                error={!!error}
                helperText={error || 'Enter ID in format: LW-XXXXXXXXX'}
                inputProps={{
                  maxLength: 12,
                  style: {
                    textTransform: 'uppercase'
                  }
                }}
                sx={{
                  mb: 2,
                  '& input': {
                    fontFamily: 'monospace',
                    fontSize: '1.1rem',
                    letterSpacing: '2px'
                  }
                }}
              />
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                fullWidth
              >
                {loading ? <CircularProgress size={24} /> : 'View Life Wheel'}
              </Button>
            </form>
          </Box>
        </Paper>
      )}

      {wheelData && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Box 
              sx={{ 
                mb: 3, 
                p: 3, 
                bgcolor: 'rgba(25, 144, 187, 0.05)', 
                borderRadius: 2,
                border: '1px solid rgba(25, 144, 187, 0.1)'
              }}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: { xs: 2, sm: 4 }, 
                  alignItems: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="subtitle2" 
                    color="primary" 
                    sx={{ 
                      mb: 1,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem'
                    }}
                  >
                    Created by
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 500,
                      color: 'text.primary'
                    }}
                  >
                    {wheelData.name}
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    width: { xs: '50%', sm: 2 }, 
                    height: { xs: 2, sm: 40 }, 
                    bgcolor: 'rgba(25, 144, 187, 0.15)',
                    borderRadius: 1
                  }} 
                />
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="subtitle2" 
                    color="primary" 
                    sx={{ 
                      mb: 1,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem'
                    }}
                  >
                    Created on
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 500,
                      color: 'text.primary'
                    }}
                  >
                    {new Date(wheelData.createdAt).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box 
              sx={{ 
                height: { xs: 600, sm: 800, md: 900 },
                width: '100%',
                mb: 3,
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'visible',
              }}
            >
              <LifeWheel 
                areas={wheelData.areas} 
                scores={wheelData.scores}
                width={800}
                height={800}
                readonly
              />
            </Box>

            <Box sx={{ borderTop: '2px solid rgba(25, 144, 187, 0.2)', pt: 3, mt: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                Satisfaction Score: {wheelData.averageScore}%
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </Container>
  );
}

export default ViewLifeWheel;
